import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Users, Megaphone, Handshake, GraduationCap, Activity, BookmarkCheck } from "lucide-react";
import Seo from "../lib/seo";
import { PETITION_URL } from "../lib/constants";
// PageHeader removed — intro block below handles the title
import Reveal from "../components/Reveal";
import PetitionCTA from "../components/PetitionCTA";
import ShareStorySection from "../components/ShareStorySection";
import ActionBar from "../components/ActionBar";

// Red heartbeat divider matching design
const RedHeartbeat = ({ className = "" }) => (
    <div className={`flex items-center gap-3 my-6 ${className}`}>
        <div className="flex-1 h-px bg-coral/40" />
        <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, type: "spring" }}
            className="w-10 h-10 rounded-full border-2 border-coral flex items-center justify-center bg-white"
        >
            <Heart className="w-4 h-4 text-coral animate-heartbeat" fill="currentColor" />
        </motion.div>
        <div className="flex-1 h-px bg-coral/40" />
    </div>
);

// Animated heartbeat ECG line for OUR MISSION section
const ECGLine = ({ className = "" }) => (
    <svg viewBox="0 0 400 40" className={className} fill="none" aria-hidden>
        <motion.path
            d="M0 20 L120 20 L135 5 L150 35 L165 10 L180 28 L195 20 L400 20"
            stroke="#E63946"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2.4, ease: "easeInOut" }}
        />
    </svg>
);

const WAYS = [
    { icon: Heart, title: "Donate", desc: "Your donation helps fund life-saving equipment, training programs, and community resources.", delay: 0 },
    { icon: Users, title: "Volunteer", desc: "Give your time and talent to help us educate, prepare, and protect more communities.", delay: 0.08 },
    { icon: Megaphone, title: "Spread Awareness", desc: "Share our mission on social media and help us reach more people with this life-saving message.", delay: 0.16 },
    { icon: Handshake, title: "Partner With Us", desc: "We partner with organizations, businesses, and communities to create a greater impact together.", delay: 0.24 },
    { icon: GraduationCap, title: "Get Trained", desc: "Learn CPR. Know AEDs. Be the reason someone gets a second chance.", delay: 0.32 },
];

const PILL_BUTTONS = [
    { label: "Give Today", to: "/donate", testid: "pill-give" },
    { label: "Sign Up", to: "/contact", testid: "pill-signup" },
    { label: "Share Our Story", to: "/our-story", testid: "pill-share" },
    { label: "Partner", to: "/contact", testid: "pill-partner" },
    { label: "Find a Class", to: "/resources", testid: "pill-class" },
];

export default function GetInvolvedPage() {
    return (
        <>
            <Seo
                title="Get Involved — Be Part of the Mission"
                description="Donate, volunteer, spread awareness, partner, or get trained. There are many ways to support the JC Law Foundation and honor JC's legacy."
                path="/get-involved"
            />

            {/* INTRO BLOCK matching reference */}
            <section className="bg-editorial py-20 md:py-24" data-testid="involved-intro">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-5">
                        <div className="overline mb-4 text-coral">Get Involved</div>
                        <h2 className="font-serif text-5xl md:text-6xl text-navy-900 leading-[1.02] tracking-tight">
                            Be Part of<br />the Mission.
                        </h2>
                        <RedHeartbeat className="!my-7 max-w-[260px]" />
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-7">
                        <p className="text-lg md:text-xl text-navy-800/85 leading-relaxed mb-8 max-w-2xl">
                            This movement exists to save lives and honor the legacy of JC. Together, we can create a world where every second counts — and no one faces a cardiac emergency alone.
                        </p>
                        <motion.p
                            initial={{ opacity: 0, scale: 0.96 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="font-serif italic text-3xl md:text-4xl text-coral flex items-center gap-3"
                        >
                            Every second matters.
                            <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
                                <Heart className="w-7 h-7 text-coral" fill="currentColor" />
                            </motion.span>
                        </motion.p>
                    </Reveal>
                </div>
            </section>

            {/* WAYS TO GET INVOLVED — 5-column red icon grid */}
            <section className="bg-editorial-muted py-24 md:py-28" data-testid="involved-ways">
                <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
                    <Reveal>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-center uppercase">
                            <span className="text-navy-900">Ways To Get </span>
                            <span className="text-coral italic">Involved</span>
                        </h2>
                        <RedHeartbeat className="!my-7 max-w-[300px] mx-auto" />
                    </Reveal>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-6">
                        {WAYS.map((w, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.7, delay: w.delay, ease: [0.21, 0.47, 0.32, 0.98] }}
                                whileHover={{ y: -6 }}
                                className="text-center px-2"
                                data-testid={`way-card-${w.title.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <motion.div
                                    whileHover={{ scale: 1.12, rotate: -4 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 16 }}
                                    className="w-20 h-20 mx-auto mb-6 border-2 border-coral rounded-full flex items-center justify-center bg-white shadow-sm"
                                >
                                    <w.icon className="w-9 h-9 text-coral" strokeWidth={1.5} />
                                </motion.div>
                                <h3 className="font-sans uppercase tracking-[0.18em] font-bold text-coral text-sm mb-4">
                                    {w.title}
                                </h3>
                                <p className="text-sm md:text-[0.95rem] text-navy-800/80 leading-relaxed max-w-[220px] mx-auto">
                                    {w.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    {/* Navy pill buttons row */}
                    <Reveal delay={0.4}>
                        <div className="mt-16 flex flex-wrap gap-3 md:gap-4 justify-center">
                            {PILL_BUTTONS.map((b) => (
                                <motion.div key={b.label} whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 300 }}>
                                    <Link
                                        to={b.to}
                                        data-testid={b.testid}
                                        className="inline-flex items-center justify-center px-7 md:px-10 py-3 md:py-3.5 bg-navy-900 hover:bg-coral text-editorial font-sans uppercase tracking-[0.18em] text-xs rounded-full transition-colors duration-300"
                                    >
                                        {b.label}
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* OUR MISSION — dark band with ECG + heart watermark */}
            <section className="relative bg-navy-950 text-editorial overflow-hidden py-24 md:py-28" data-testid="involved-mission">
                <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />
                {/* Large heart watermark on right */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 0.15, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: "easeOut" }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none"
                    aria-hidden
                >
                    <svg viewBox="0 0 400 360" className="w-[360px] md:w-[460px] h-auto text-white" fill="none">
                        <path
                            d="M200 320 C 200 320, 40 230, 40 130 C 40 84, 76 50, 120 50 C 156 50, 184 74, 200 102 C 216 74, 244 50, 280 50 C 324 50, 360 84, 360 130 C 360 230, 200 320, 200 320 Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                        />
                        <motion.path
                            d="M20 180 L140 180 L155 150 L172 220 L188 130 L205 200 L220 170 L380 170"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            fill="none"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 2, delay: 0.5 }}
                        />
                    </svg>
                </motion.div>

                <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10">
                    <Reveal>
                        <p className="overline mb-4 text-coral">Our Mission</p>
                        <div className="grid lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-2 text-coral">
                                <ECGLine className="w-full" />
                            </div>
                            <h2 className="lg:col-span-10 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                                Prevent. Prepare. <span className="text-coral">Save Lives.</span>
                            </h2>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-8 max-w-3xl text-lg md:text-xl text-editorial/85 leading-relaxed">
                            We are committed to raising awareness about cardiac arrest, improving emergency preparedness, and ensuring that life-saving resources are accessible to everyone, everywhere.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* EVERY ACTION CREATES IMPACT + REAL PEOPLE REAL STORIES */}
            <section className="bg-editorial py-24 md:py-28" data-testid="involved-impact">
                <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
                    <Reveal className="lg:col-span-7">
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy-900 leading-tight">
                            Every action creates <span className="text-coral italic">impact.</span>
                        </h2>
                        <div className="mt-5 w-14 h-px bg-coral" />
                        <p className="mt-8 text-lg text-navy-800/85 leading-relaxed max-w-xl">
                            Whether you give, volunteer, or simply share, you are helping build a safer, stronger community and carrying forward JC's legacy of service, courage, and compassion.
                        </p>
                        <motion.p
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="mt-8 font-serif italic text-2xl md:text-3xl text-coral flex items-center gap-3"
                        >
                            Together, we can save more lives.
                            <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
                                <Heart className="w-6 h-6 text-coral" fill="currentColor" />
                            </motion.span>
                        </motion.p>
                    </Reveal>

                    {/* REAL PEOPLE card */}
                    <motion.aside
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, delay: 0.15, ease: [0.21, 0.47, 0.32, 0.98] }}
                        className="lg:col-span-5 bg-editorial-muted border border-coral/30 p-10 md:p-12 text-center shadow-sm"
                    >
                        {/* Heart with ECG line */}
                        <div className="flex items-center justify-center mb-5">
                            <ECGLine className="w-24 text-coral" />
                            <Heart className="w-9 h-9 text-coral -mx-2 animate-heartbeat" fill="currentColor" />
                            <ECGLine className="w-24 text-coral scale-x-[-1]" />
                        </div>
                        <p className="font-sans uppercase tracking-[0.22em] text-sm font-bold text-coral mb-5">
                            Real People. Real Stories.
                        </p>
                        <p className="text-navy-800/85 leading-relaxed text-base mb-8 max-w-md mx-auto">
                            Behind every statistic is a family, a friend, a moment that changed everything. This is why we do what we do.
                        </p>
                        <Link
                            to="/our-story"
                            data-testid="view-stories-btn"
                            className="inline-flex items-center justify-center px-10 py-3.5 bg-navy-900 hover:bg-coral text-editorial font-sans uppercase tracking-[0.18em] text-xs rounded-full transition-colors duration-300"
                        >
                            View Stories
                        </Link>
                    </motion.aside>
                </div>
            </section>

            {/* Sign petition stripe */}
            <Reveal>
                <div className="bg-navy-900 py-12 text-center" data-testid="involved-petition-stripe">
                    <a
                        href={PETITION_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-3 px-10 py-4 bg-coral hover:bg-coral-dark text-white font-sans uppercase tracking-[0.18em] text-xs transition-colors duration-300"
                    >
                        <BookmarkCheck className="w-4 h-4" />
                        Sign the Petition
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>
            </Reveal>

            <ShareStorySection />
            <ActionBar />
        </>
    );
}
